import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBooking } from '@/api/hooks/useBookings';
import { useCreditBalance } from '@/api/hooks/useCredit';
import { useAuth } from '@/providers/AuthProvider';
import { formatCurrency, formatLocalTime } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import UnlockDialog from '@/components/booking/UnlockDialog';
import PriceBreakdown from '@/components/booking/PriceBreakdown';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { useVenueContext } from '@/providers/VenueProvider';

export default function ConfirmationPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { currentSlug } = useVenueContext();
  const { data, isLoading, error } = useBooking(id || '');
  const { data: creditData } = useCreditBalance(isAuthenticated);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const creditBalance = creditData?.balance || 0;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-md space-y-6 px-4 pt-8 text-center">
        <Skeleton className="mx-auto h-16 w-16 rounded-full" />
        <Skeleton className="mx-auto h-8 w-48" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-md px-4 pt-8">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {t('common.error')}
        </div>
      </div>
    );
  }

  const { booking } = data;
  const access = booking.access;

  return (
    <div className="mx-auto max-w-md space-y-6 px-4 pt-8 text-center">
      {/* Success header */}
      <div>
        <CheckCircle2 className="mx-auto h-16 w-16 text-green-500 animate-[pulse_2s_ease-in-out_1]" />
        <h1 className="mt-3 text-2xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          {t('booking.confirmed')}
        </h1>
      </div>

      {/* Access code card — the hero */}
      {access && (
        <Card className="border-2 border-primary bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 to-primary/[0.02]">
          <CardContent className="space-y-4 p-8">
            <p className="text-sm uppercase tracking-wide text-muted-foreground">
              {t('booking.access_code')}
            </p>
            <p className="text-5xl font-mono font-bold tracking-[0.3em]">
              {access.code}
            </p>
            {access.instructions && (
              <p className="text-sm text-muted-foreground">
                {access.instructions}
              </p>
            )}
            {access.code && (
              <UnlockDialog
                accessCode={access.code}
                bookingStartTime={booking.startTime}
                startTimeLocal={booking.startTimeLocal}
                unlockAvailableAt={access.unlockAvailableAt}
                unitName={booking.unitName}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Booking summary — no labels, self-explanatory */}
      <Card>
        <CardContent className="space-y-3 p-6">
          <p className="text-lg font-semibold">{t(`sport_${booking.sportCode}`, { defaultValue: booking.sportName })}</p>
          <p className="text-xl font-medium">
            {formatLocalTime(booking.startTimeLocal, i18n.language)} → {formatLocalTime(booking.endTimeLocal, i18n.language)}
          </p>
          <p className="text-muted-foreground">
            {new Date(booking.startTime).toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
          <p className="text-sm text-muted-foreground">
            {booking.unitName} · {booking.duration} min
            {booking.originalDuration && booking.originalDuration !== booking.duration && (
              <span className="text-xs ml-1">
                ({booking.originalDuration} min + {booking.duration - booking.originalDuration} min extended)
              </span>
            )}
            {booking.playerCount > 1 && ` · ${booking.playerCount} players`}
          </p>
          <p className="pt-2 text-xl font-mono font-semibold">
            {formatCurrency(booking.pricing.total, booking.pricing.currency, i18n.language)}
          </p>

          {/* Collapsible price breakdown */}
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="flex items-center gap-1 text-sm text-primary hover:underline mx-auto pt-2 whitespace-nowrap"
          >
            {t('booking.view_price_breakdown')}
            {showBreakdown ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          {showBreakdown && (
            <PriceBreakdown booking={booking} />
          )}
        </CardContent>
      </Card>

      {/* Credit upsell */}
      {creditBalance === 0 && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="flex items-center gap-3 p-4">
            <Sparkles className="h-5 w-5 text-primary shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">{t('credit.confirmation_prompt')}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/credits')}>
              {t('credit.learn_more')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-3">
        <Button variant="outline" className="flex-1 whitespace-nowrap" onClick={() => navigate('/bookings')}>
          {t('booking.view_my_bookings')}
        </Button>
        <Button
          className="flex-1 whitespace-nowrap"
          onClick={() => navigate(`/venue/${currentSlug}/book`)}
        >
          {t('booking.book_another')}
        </Button>
      </div>
    </div>
  );
}
